import { NvButton, NvModal, NvSelectDropdown, } from '@simwood/nv-react-components-v2';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAudioDevices } from '../helpers/getAudioDevices';
import { getInboundRinging, getInboundRingtoneFileName, inboundRingtones, setInboundRingtone, } from '../helpers/ringing';
import { selectAudioInputDevice, selectAudioOutputDevice, selectRingingOutputDevice, } from '../reducers/client';
import styles from '../styles/audioDeviceModal.scss';
import Popover from './Popover';
import Pause from './icons/Hold';
import Play from './icons/Play';
const audioDeviceToNvSelectOption = (audioDevice) => {
    if (!audioDevice)
        return undefined;
    return { label: audioDevice.label, value: audioDevice.deviceId };
};
const AudioDeviceModal = (props) => {
    var _a;
    const dispatch = useDispatch();
    const selectedAudioInputDeviceId = useSelector((state) => state.client.selectedAudioInputDeviceId);
    const selectedAudioOutputDeviceId = useSelector((state) => state.client.selectedAudioOutputDeviceId);
    const selectedRingingOutputDeviceId = useSelector((state) => state.client.selectedRingingOutputDeviceId);
    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState();
    const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState();
    const [selectedRingingOutputDevice, setSelectedRingingOutputDevice] = useState();
    const [selectedInboundRingtone, setSelectedInboundRingtone] = useState(getInboundRingtoneFileName());
    const [ringingRingtone, setRingingRingtone] = useState('');
    const [error, setError] = useState(null);
    const getDevices = () => {
        getAudioDevices({
            selectedAudioInputDeviceId,
            selectedAudioOutputDeviceId,
            selectedRingingOutputDeviceId,
        })
            .then((devices) => {
            const { audioInputDevices, audioOutputDevices, selectedAudioInputDevice, selectedAudioOutputDevice, selectedRingingOutputDevice, } = devices;
            if (audioInputDevices.length === 0) {
                setError({
                    title: 'No audio input devices available.',
                    message: 'To make and receive calls please connect a microphone and then refresh this page.',
                });
                return;
            }
            if (audioOutputDevices.length === 0) {
                setError({
                    title: 'No audio output devices available.',
                    message: 'To make and receive calls please connect headphones or a speaker and then refresh this page.',
                });
                return;
            }
            setAudioInputDevices(audioInputDevices);
            setAudioOutputDevices(audioOutputDevices);
            setSelectedAudioInputDevice(selectedAudioInputDevice);
            setSelectedAudioOutputDevice(selectedAudioOutputDevice);
            setSelectedRingingOutputDevice(selectedRingingOutputDevice);
        })
            .catch((err) => {
            console.error('Error getting audio devices: ', err);
        });
    };
    useEffect(() => {
        getDevices();
        return () => {
            const inboundRinging = getInboundRinging(true);
            if (!inboundRinging.paused) {
                getInboundRinging(true).pause();
            }
        };
    }, []);
    const onCancelClicked = () => {
        const inboundRinging = getInboundRinging(true);
        if (!inboundRinging.paused) {
            getInboundRinging(true).pause();
        }
        props.onCancel();
    };
    const onSaveClicked = () => {
        selectedAudioInputDevice &&
            dispatch(selectAudioInputDevice(selectedAudioInputDevice.deviceId));
        selectedAudioOutputDevice &&
            dispatch(selectAudioOutputDevice(selectedAudioOutputDevice.deviceId));
        selectedRingingOutputDevice &&
            dispatch(selectRingingOutputDevice(selectedRingingOutputDevice.deviceId));
        setInboundRingtone(selectedInboundRingtone);
        props.onSave();
    };
    const inboundRinging = getInboundRinging(true);
    if (error && error.title) {
        return (React.createElement(Popover, { title: error.title, message: error.message, type: 'danger' }));
    }
    const pauseRinging = () => {
        inboundRinging.pause();
        inboundRinging.currentTime = 0;
        setRingingRingtone('');
    };
    return (React.createElement(NvModal, { size: 'standard', title: 'Audio Devices', type: 'action', show: true, allowClickOutside: true, buttons: [
            {
                kind: 'neutral',
                onClick: onCancelClicked,
                text: 'CANCEL',
            },
            {
                onClick: onSaveClicked,
                text: 'SAVE',
            },
        ], onClose: onCancelClicked },
        React.createElement("div", { className: styles.row },
            React.createElement("span", null, "Microphone"),
            React.createElement(NvSelectDropdown, { options: audioInputDevices
                    .map(audioDeviceToNvSelectOption)
                    .filter((x) => !!x), value: audioDeviceToNvSelectOption(audioInputDevices.find((x) => selectedAudioInputDevice &&
                    x.deviceId === selectedAudioInputDevice.deviceId)), onChange: (option) => {
                    const value = option[0].value;
                    const device = audioInputDevices.find((x) => x.deviceId === value);
                    setSelectedAudioInputDevice(device);
                }, isClearable: false, isSearchable: false })),
        React.createElement("div", { className: styles.row },
            React.createElement("span", null, "In-call audio output"),
            React.createElement(NvSelectDropdown, { options: audioOutputDevices
                    .map(audioDeviceToNvSelectOption)
                    .filter((x) => !!x), value: audioDeviceToNvSelectOption(audioOutputDevices.find((x) => selectedAudioOutputDevice &&
                    x.deviceId === selectedAudioOutputDevice.deviceId)), onChange: (option) => {
                    const value = option[0].value;
                    const device = audioOutputDevices.find((x) => x.deviceId === value);
                    setSelectedAudioOutputDevice(device);
                }, isClearable: false, isSearchable: false })),
        React.createElement("div", { className: styles.row },
            React.createElement("span", null, "Ringing audio output"),
            React.createElement(NvSelectDropdown, { options: audioOutputDevices
                    .map(audioDeviceToNvSelectOption)
                    .filter((x) => !!x), value: audioDeviceToNvSelectOption(audioOutputDevices.find((x) => selectedRingingOutputDevice &&
                    x.deviceId === selectedRingingOutputDevice.deviceId)), onChange: (option) => {
                    const value = option[0].value;
                    const device = audioOutputDevices.find((x) => x.deviceId === value);
                    setSelectedRingingOutputDevice(device);
                }, isClearable: false, isSearchable: false })),
        React.createElement("div", { className: styles.row },
            React.createElement("span", null, "Ringtone"),
            React.createElement(NvSelectDropdown, { options: inboundRingtones.map((ringtone) => ({
                    label: ringtone.name,
                    value: ringtone.fileName,
                })), value: (_a = inboundRingtones.find((r) => r.fileName === selectedInboundRingtone)) === null || _a === void 0 ? void 0 : _a.name, onChange: (option) => {
                    const value = option[0].value;
                    setInboundRingtone(value, true);
                    setSelectedInboundRingtone(value);
                }, isClearable: false, isSearchable: false, onMenuClose: () => {
                    if (ringingRingtone) {
                        pauseRinging();
                    }
                }, OptionElement: ({ data }) => (React.createElement("div", { className: styles.ringtoneOption },
                    React.createElement("span", null, data.label),
                    React.createElement(NvButton, { rounded: true, className: [
                            styles.button,
                            ringingRingtone === data.value
                                ? styles.pauseButton
                                : styles.playButton,
                        ].join(' '), inverted: ringingRingtone !== data.value, onClick: (e) => {
                            e.stopPropagation();
                            if (ringingRingtone === data.value) {
                                pauseRinging();
                            }
                            else {
                                setInboundRingtone(data.value, true);
                                inboundRinging.play();
                                setRingingRingtone(data.value);
                            }
                        } }, ringingRingtone === data.value ? React.createElement(Pause, null) : React.createElement(Play, null)))) }))));
};
export default AudioDeviceModal;
